import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Eye, Cookie, UserCheck, Lock } from "lucide-react";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-black">
      <SEO
        title="Kebijakan Privasi | Putri Henna Artist"
        description="Kebijakan privasi Putri Henna Artist. Pelajari bagaimana kami mengumpulkan, menggunakan, dan melindungi informasi pribadi Anda."
        keywords="kebijakan privasi, privacy policy, putri henna, perlindungan data"
        url="https://tehputrihenna.com/privacy-policy"
      />
      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#D4AF7A]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-[#C9A882]/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-4xl px-4 py-12 mx-auto sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-[#F7F5F2]/70 hover:text-[#D4AF7A] transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          Kembali ke Beranda
        </button>

        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF7A]/10 rounded-full mb-6">
            <Shield className="w-8 h-8 text-[#D4AF7A]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#F7F5F2] mb-4">
            Kebijakan Privasi
          </h1>
          <p className="text-[#F7F5F2]/70 text-lg">
            Terakhir diperbarui:{" "}
            {new Date().toLocaleDateString("id-ID", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <p className="text-[#F7F5F2]/80 leading-relaxed">
              Putri Henna Artist ("kami", "kita") berkomitmen untuk melindungi
              dan menghormati privasi Anda. Dokumen Kebijakan Privasi ini
              menjelaskan bagaimana kami mengumpulkan, menggunakan, dan
              melindungi informasi pribadi Anda saat Anda menggunakan website
              dan layanan kami.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <Eye className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Informasi yang Kami Kumpulkan
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-[#D4AF7A] mb-2">
                  Informasi yang Anda Berikan
                </h3>
                <ul className="space-y-2 text-[#F7F5F2]/70">
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      <strong>Nama dan informasi kontak</strong> saat Anda
                      mengirim testimoni atau menghubungi kami
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      <strong>Foto profil</strong> (opsional) yang Anda berikan
                      bersama testimoni
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      <strong>Testimoni dan ulasan</strong> yang Anda kirimkan
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      <strong>Informasi layanan</strong> seperti jenis acara
                      (wedding, engagement, dll)
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[#D4AF7A] mb-2">
                  Informasi yang Dikumpulkan Secara Otomatis
                </h3>
                <ul className="space-y-2 text-[#F7F5F2]/70">
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      <strong>Informasi perangkat</strong> seperti jenis
                      perangkat, sistem operasi, dan browser
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      <strong>Informasi penggunaan</strong> seperti halaman yang
                      dikunjungi dan waktu kunjungan
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF7A] mt-1">•</span>
                    <span>
                      <strong>Alamat IP</strong> dan lokasi perkiraan
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <UserCheck className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Bagaimana Kami Menggunakan Informasi Anda
              </h2>
            </div>

            <ul className="space-y-3 text-[#F7F5F2]/70">
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">✓</span>
                <span>Menyediakan dan meningkatkan layanan kami</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">✓</span>
                <span>Memproses permintaan dan reservasi layanan henna</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">✓</span>
                <span>
                  Menampilkan testimoni di website kami (dengan persetujuan
                  Anda)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">✓</span>
                <span>
                  Merespon pertanyaan dan permintaan dukungan pelanggan
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">✓</span>
                <span>
                  Menganalisis penggunaan website untuk meningkatkan pengalaman
                  pengguna
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">✓</span>
                <span>
                  Mengirim komunikasi terkait layanan (dengan persetujuan Anda)
                </span>
              </li>
            </ul>
          </section>

          {/* Sharing of Information */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <Lock className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Perbagian Informasi
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Kami tidak menjual, memperdagangkan, atau menyewakan informasi
              pribadi Anda kepada pihak ketiga. Namun, kami dapat membagikan
              informasi Anda dalam circumstances berikut:
            </p>

            <ul className="space-y-3 text-[#F7F5F2]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Dengan persetujuan Anda</strong> - Kami akan
                  mengungkapkan informasi jika Anda memberikan persetujuan
                  eksplisit
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Penyedia layanan</strong> - Kami dapat menggunakan
                  penyedia layanan pihak ketiga untuk mengoperasikan website
                  kami
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Untuk keamanan</strong> - Kami dapat membagikan
                  informasi untuk melindungi hak, properti, atau keamanan kami
                  atau pengguna lain
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>
                  <strong>Kewajiban hukum</strong> - Jika diwajibkan oleh hukum,
                  kami dapat mengungkapkan informasi untuk mematuhi kewajiban
                  hukum
                </span>
              </li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <Cookie className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Cookies dan Teknologi Pelacakan
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Kami menggunakan cookies dan teknologi serupa untuk:
            </p>

            <ul className="space-y-2 text-[#F7F5F2]/70 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Mengingat preferensi dan pengaturan Anda</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Menganalisis penggunaan website</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Memahami pola trafik dan tren</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Menyediakan fitur autentikasi dan keamanan</span>
              </li>
            </ul>

            <p className="text-[#F7F5F2]/80">
              Anda dapat mengontrol cookies melalui pengaturan browser Anda,
              namun menonaktifkan cookies dapat mempengaruhi fungsionalitas
              website.
            </p>
          </section>

          {/* Data Security */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <Shield className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Keamanan Data
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Kami mengambil langkah-langkah keamanan yang wajar untuk
              melindungi informasi pribadi Anda, termasuk:
            </p>

            <ul className="space-y-2 text-[#F7F5F2]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Enkripsi data saat transmisi dan penyimpanan</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Akses terbatas ke informasi pribadi</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Audit dan peninjauan keamanan secara berkala</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF7A] mt-1">•</span>
                <span>Compliance dengan standar keamanan industri</span>
              </li>
            </ul>

            <p className="text-[#F7F5F2]/60 text-sm mt-4">
              Namun, tidak ada metode transmisi atau penyimpanan yang 100% aman.
              Kami tidak dapat menjamin keamanan absolut informasi Anda.
            </p>
          </section>

          {/* Your Rights */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[#D4AF7A]/10 rounded-lg">
                <UserCheck className="w-6 h-6 text-[#D4AF7A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">
                Hak Privasi Anda
              </h2>
            </div>

            <p className="text-[#F7F5F2]/80 mb-4">
              Anda memiliki hak terkait informasi pribadi Anda:
            </p>

            <ul className="space-y-3 text-[#F7F5F2]/70">
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">→</span>
                <span>
                  <strong>Akses</strong> - Meminta salinan informasi pribadi
                  yang kami simpan
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">→</span>
                <span>
                  <strong>Koreksi</strong> - Memperbarui atau memperbaiki
                  informasi yang tidak akurat
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">→</span>
                <span>
                  <strong>Hapus</strong> - Meminta penghapusan informasi pribadi
                  Anda
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">→</span>
                <span>
                  <strong>Menolak</strong> - Menolak pemrosesan informasi
                  tertentu
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF7A] mt-1">→</span>
                <span>
                  <strong>Portabilitas</strong> - Menerima informasi dalam
                  format terstruktur
                </span>
              </li>
            </ul>

            <p className="text-[#F7F5F2]/80 mt-4">
              Untuk menggunakan hak ini, silakan hubungi kami melalui informasi
              kontak di bawah.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <h2 className="text-xl font-bold text-[#F7F5F2] mb-4">
              Privasi Anak
            </h2>
            <p className="text-[#F7F5F2]/80">
              Website dan layanan kami tidak ditujukan untuk anak di bawah 13
              tahun. Kami tidak dengan sengaja mengumpulkan informasi pribadi
              dari anak di bawah 13 tahun. Jika kami mengetahui bahwa kami telah
              mengumpulkan informasi dari anak di bawah 13 tahun tanpa
              persetujuan orang tua, kami akan mengambil langkah untuk menghapus
              informasi tersebut.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <h2 className="text-xl font-bold text-[#F7F5F2] mb-4">
              Perubahan Kebijakan Ini
            </h2>
            <p className="text-[#F7F5F2]/80">
              Kami dapat memperbarui Kebijakan Privasi ini dari waktu ke waktu.
              Perubahan akan diposting di halaman ini dengan tanggal revisi yang
              diperbarui. Kami menyarankan Anda untuk meninjau kebijakan ini
              secara berkala.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-gradient-to-r from-[#C9A882]/10 to-[#D4AF7A]/10 backdrop-blur-sm border border-[#D4AF7A]/30 rounded-2xl p-8">
            <h2 className="text-xl font-bold text-[#F7F5F2] mb-4">
              Hubungi Kami
            </h2>
            <p className="text-[#F7F5F2]/80 mb-4">
              Jika Anda memiliki pertanyaan, concerns, atau request terkait
              Kebijakan Privasi ini atau praktik privasi kami, silakan hubungi
              kami:
            </p>
            <div className="space-y-2 text-[#F7F5F2]/70">
              <p>
                <strong className="text-[#D4AF7A]">Email:</strong>{" "}
                putrileony.office@gmail.com
              </p>
              <p>
                <strong className="text-[#D4AF7A]">Website:</strong>{" "}
                tehputrihenna.com
              </p>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center text-sm text-[#F7F5F2]/50">
          <p>
            Dengan menggunakan website dan layanan kami, Anda mengakui bahwa
            Anda telah membaca, memahami, dan menyetujui Kebijakan Privasi ini.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
